import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FormattedTextarea } from '@/components/ui/formatted-textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, X, Link as LinkIcon, Briefcase, GraduationCap, ExternalLink, Github, Image } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const formSchema = z.object({
  title: z.string().min(1, { message: 'Title is required' }),
  description: z.string().optional(),
  github_url: z.string().url({ message: 'Please enter a valid URL' }).optional().or(z.literal('')),
  url: z.string().url({ message: 'Please enter a valid URL' }).optional().or(z.literal('')),
  image_url: z.string().url({ message: 'Please enter a valid URL' }).optional().or(z.literal('')),
  technologies: z.array(z.string()).optional(),
  project_experiences: z.array(z.object({
    id: z.string(),
    primary_relation: z.boolean().default(false),
    contribution_percentage: z.number().min(0).max(100).default(100),
    role: z.string().optional(),
  })).optional(),
  project_education: z.array(z.object({
    id: z.string(),
    primary_relation: z.boolean().default(false),
    contribution_percentage: z.number().min(0).max(100).default(100),
    role: z.string().optional(),
  })).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ProjectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: any;
  onSuccess: () => void;
}

export default function ProjectForm({ open, onOpenChange, project, onSuccess }: ProjectFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [newTech, setNewTech] = useState('');
  const [experiences, setExperiences] = useState<any[]>([]);
  const [education, setEducation] = useState<any[]>([]);
  const isEditing = !!project;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      github_url: '',
      url: '',
      image_url: '',
      technologies: [],
      project_experiences: [],
      project_education: [],
    },
  });

  useEffect(() => {
    if (!user) return;

    const fetchRelatedData = async () => {
      try {
        const { data: experiencesData } = await supabase
          .from('experiences')
          .select('*')
          .eq('profile_id', user.id)
          .order('start_date', { ascending: false });

        if (experiencesData) {
          setExperiences(experiencesData);
        }

        const { data: educationData } = await supabase
          .from('education')
          .select('*')
          .eq('profile_id', user.id)
          .order('start_date', { ascending: false });

        if (educationData) {
          setEducation(educationData);
        }
      } catch (error) {
        console.error('Error fetching related data:', error);
      }
    };

    fetchRelatedData();
  }, [user]);

  useEffect(() => {
    if (open) {
      if (isEditing && project) {
        const fetchProjectRelations = async () => {
          try {
            const { data: expRelations } = await supabase
              .from('project_experiences')
              .select(`
                id,
                experience_id,
                primary_relation,
                contribution_percentage,
                role
              `)
              .eq('project_id', project.id);

            const { data: eduRelations } = await supabase
              .from('project_education')
              .select(`
                id,
                education_id,
                primary_relation,
                contribution_percentage,
                role
              `)
              .eq('project_id', project.id);

            form.reset({
              ...project,
              project_experiences: expRelations?.map(rel => ({
                id: rel.experience_id,
                primary_relation: rel.primary_relation,
                contribution_percentage: rel.contribution_percentage,
                role: rel.role,
              })) || [],
              project_education: eduRelations?.map(rel => ({
                id: rel.education_id,
                primary_relation: rel.primary_relation,
                contribution_percentage: rel.contribution_percentage,
                role: rel.role,
              })) || [],
            });
          } catch (error) {
            console.error('Error fetching project relations:', error);
          }
        };

        fetchProjectRelations();
      } else {
        form.reset({
          title: '',
          description: '',
          github_url: '',
          url: '',
          image_url: '',
          technologies: [],
          project_experiences: [],
          project_education: [],
        });
      }
      setNewTech('');
    }
  }, [open, project, form, isEditing]);

  const onSubmit = async (data: FormValues) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const projectData = {
        title: data.title,
        description: data.description,
        github_url: data.github_url,
        url: data.url,
        image_url: data.image_url,
        technologies: data.technologies,
        profile_id: user.id,
      };

      let projectId: string;
      
      if (isEditing) {
        const { error: updateError } = await supabase
          .from('projects')
          .update(projectData)
          .eq('id', project.id);

        if (updateError) throw updateError;
        projectId = project.id;
      } else {
        const { data: insertedProject, error: insertError } = await supabase
          .from('projects')
          .insert([projectData])
          .select('id')
          .single();

        if (insertError) throw insertError;
        projectId = insertedProject.id;
      }

      if (data.project_experiences && data.project_experiences.length > 0) {
        await supabase
          .from('project_experiences')
          .delete()
          .eq('project_id', projectId);

        const { error: expError } = await supabase
          .from('project_experiences')
          .insert(
            data.project_experiences.map(exp => ({
              project_id: projectId,
              experience_id: exp.id,
              primary_relation: exp.primary_relation,
              contribution_percentage: exp.contribution_percentage,
              role: exp.role,
            }))
          );

        if (expError) throw expError;
      }

      if (data.project_education && data.project_education.length > 0) {
        await supabase
          .from('project_education')
          .delete()
          .eq('project_id', projectId);

        const { error: eduError } = await supabase
          .from('project_education')
          .insert(
            data.project_education.map(edu => ({
              project_id: projectId,
              education_id: edu.id,
              primary_relation: edu.primary_relation,
              contribution_percentage: edu.contribution_percentage,
              role: edu.role,
            }))
          );

        if (eduError) throw eduError;
      }

      toast({
        title: 'Success',
        description: `Project ${isEditing ? 'updated' : 'added'} successfully`,
      });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error saving project:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addTechnology = () => {
    if (!newTech.trim()) return;
    
    const currentTechs = form.getValues('technologies') || [];
    if (!currentTechs.includes(newTech.trim())) {
      form.setValue('technologies', [...currentTechs, newTech.trim()]);
    }
    setNewTech('');
  };

  const removeTechnology = (tech: string) => {
    const currentTechs = form.getValues('technologies') || [];
    form.setValue('technologies', currentTechs.filter(t => t !== tech));
  };

  const handleExperienceChange = (experienceId: string, field: string, value: any) => {
    const experiences = form.getValues('project_experiences') || [];
    const index = experiences.findIndex(exp => exp.id === experienceId);
    
    if (index === -1) {
      experiences.push({
        id: experienceId,
        primary_relation: field === 'primary_relation' ? value : false,
        contribution_percentage: field === 'contribution_percentage' ? value : 100,
        role: field === 'role' ? value : '',
      });
    } else {
      experiences[index] = {
        ...experiences[index],
        [field]: value,
      };
    }
    
    form.setValue('project_experiences', experiences);
  };

  const handleEducationChange = (educationId: string, field: string, value: any) => {
    const education = form.getValues('project_education') || [];
    const index = education.findIndex(edu => edu.id === educationId);
    
    if (index === -1) {
      education.push({
        id: educationId,
        primary_relation: field === 'primary_relation' ? value : false,
        contribution_percentage: field === 'contribution_percentage' ? value : 100,
        role: field === 'role' ? value : '',
      });
    } else {
      education[index] = {
        ...education[index],
        [field]: value,
      };
    }
    
    form.setValue('project_education', education);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Project' : 'Add Project'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update your project details below.' : 'Add your project details below.'}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Project Details</TabsTrigger>
                <TabsTrigger value="relations">Relations</TabsTrigger>
                <TabsTrigger value="links">Links & Media</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-6 mt-4">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Title</FormLabel>
                        <FormControl>
                          <Input placeholder="My Awesome Project" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <FormattedTextarea
                            placeholder="Describe your project..."
                            value={field.value || ''}
                            onValueChange={(value) => field.onChange(value)}
                          />
                        </FormControl>
                        <FormDescription>
                          Provide a brief description of your project, its purpose, and your role.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-4">
                    <FormLabel>Technologies Used</FormLabel>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a technology (e.g., React, Python, AWS)"
                        value={newTech}
                        onChange={(e) => setNewTech(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTechnology();
                          }
                        }}
                      />
                      <Button 
                        type="button" 
                        onClick={addTechnology}
                        variant="secondary"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[40px] p-4 bg-muted/30 rounded-lg">
                      {form.watch('technologies')?.map((tech, index) => (
                        <Badge 
                          key={index} 
                          variant="secondary" 
                          className="flex items-center gap-1 px-3 py-1 text-sm"
                        >
                          {tech}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                            onClick={() => removeTechnology(tech)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                      {!form.watch('technologies')?.length && (
                        <p className="text-sm text-muted-foreground">
                          No technologies added yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="relations" className="space-y-6 mt-4">
                {experiences.length > 0 && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-primary" />
                        <CardTitle>Work Experience</CardTitle>
                      </div>
                      <CardDescription>
                        Link this project to your work experience
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {experiences.map((exp) => {
                        const relation = form.watch('project_experiences')?.find(e => e.id === exp.id);
                        return (
                          <div 
                            key={exp.id} 
                            className={`space-y-4 border rounded-lg p-4 transition-colors ${
                              relation ? 'border-primary/50 bg-primary/5' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium">{exp.position}</h4>
                                <p className="text-sm text-muted-foreground">{exp.company}</p>
                              </div>
                              <Checkbox
                                checked={!!relation}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    handleExperienceChange(exp.id, 'primary_relation', false);
                                  } else {
                                    const experiences = form.getValues('project_experiences') || [];
                                    form.setValue(
                                      'project_experiences',
                                      experiences.filter(e => e.id !== exp.id)
                                    );
                                  }
                                }}
                              />
                            </div>

                            {relation && (
                              <div className="space-y-4 pt-2">
                                <div className="space-y-2">
                                  <FormLabel className="text-sm">Contribution</FormLabel>
                                  <div className="flex items-center gap-4">
                                    <Slider
                                      value={[relation.contribution_percentage]}
                                      onValueChange={(value) => 
                                        handleExperienceChange(exp.id, 'contribution_percentage', value[0])
                                      }
                                      max={100}
                                      step={1}
                                      className="flex-1"
                                    />
                                    <span className="text-sm font-medium w-12 text-right">
                                      {relation.contribution_percentage}%
                                    </span>
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <FormLabel className="text-sm">Role</FormLabel>
                                  <Input
                                    value={relation.role || ''}
                                    onChange={(e) => 
                                      handleExperienceChange(exp.id, 'role', e.target.value)
                                    }
                                    placeholder="Your role in this project"
                                  />
                                </div>

                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={relation.primary_relation}
                                    onCheckedChange={(checked) => 
                                      handleExperienceChange(exp.id, 'primary_relation', checked)
                                    }
                                  />
                                  <FormLabel className="text-sm">
                                    Primary work experience for this project
                                  </FormLabel>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}

                {education.length > 0 && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-5 w-5 text-primary" />
                        <CardTitle>Education</CardTitle>
                      </div>
                      <CardDescription>
                        Link this project to your education
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {education.map((edu) => {
                        const relation = form.watch('project_education')?.find(e => e.id === edu.id);
                        return (
                          <div 
                            key={edu.id} 
                            className={`space-y-4 border rounded-lg p-4 transition-colors ${
                              relation ? 'border-primary/50 bg-primary/5' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium">{edu.degree}</h4>
                                <p className="text-sm text-muted-foreground">{edu.institution}</p>
                              </div>
                              <Checkbox
                                checked={!!relation}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    handleEducationChange(edu.id, 'primary_relation', false);
                                  } else {
                                    const education = form.getValues('project_education') || [];
                                    form.setValue(
                                      'project_education',
                                      education.filter(e => e.id !== edu.id)
                                    );
                                  }
                                }}
                              />
                            </div>

                            {relation && (
                              <div className="space-y-4 pt-2">
                                <div className="space-y-2">
                                  <FormLabel className="text-sm">Contribution</FormLabel>
                                  <div className="flex items-center gap-4">
                                    <Slider
                                      value={[relation.contribution_percentage]}
                                      onValueChange={(value) => 
                                        handleEducationChange(edu.id, 'contribution_percentage', value[0])
                                      }
                                      max={100}
                                      step={1}
                                      className="flex-1"
                                    />
                                    <span className="text-sm font-medium w-12 text-right">
                                      {relation.contribution_percentage}%
                                    </span>
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <FormLabel className="text-sm">Role</FormLabel>
                                  <Input
                                    value={relation.role || ''}
                                    onChange={(e) => 
                                      handleEducationChange(edu.id, 'role', e.target.value)
                                    }
                                    placeholder="Your role in this project"
                                  />
                                </div>

                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={relation.primary_relation}
                                    onCheckedChange={(checked) => 
                                      handleEducationChange(edu.id, 'primary_relation', checked)
                                    }
                                  />
                                  <FormLabel className="text-sm">
                                    Primary education project
                                  </FormLabel>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}

                {!experiences.length && !education.length && (
                  <div className="text-center py-8 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">
                      Add work experience or education entries to link them to this project
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="links" className="space-y-6 mt-4">
                <div className="grid gap-6">
                  <FormField
                    control={form.control}
                    name="github_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>GitHub Repository</FormLabel>
                        <FormControl>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Github className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input 
                                placeholder="https://github.com/username/project" 
                                className="pl-10"
                                {...field} 
                              />
                            </div>
                            {field.value && (
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="icon"
                                asChild
                              >
                                <a 
                                  href={field.value} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Live Demo</FormLabel>
                        <FormControl>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input 
                                placeholder="https://myproject.com" 
                                className="pl-10"
                                {...field} 
                              />
                            </div>
                            {field.value && (
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="icon"
                                asChild
                              >
                                <a 
                                  href={field.value} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="image_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Image</FormLabel>
                        <FormControl>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Image className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input 
                                placeholder="https://example.com/image.jpg" 
                                className="pl-10"
                                {...field} 
                              />
                            </div>
                            {field.value && (
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="icon"
                                asChild
                              >
                                <a 
                                  href={field.value} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                          </div>
                        </FormControl>
                        <FormDescription>
                          Provide a URL to an image that showcases your project
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditing ? 'Updating...' : 'Adding...'}
                  </>
                ) : (
                  isEditing ? 'Update Project' : 'Add Project'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}